import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Machine images
import werkstatt from '@/assets/machines/werkstatt.png';
import hurcoVmx42ui from '@/assets/machines/hurco-vmx42ui.jpg';
import hurcoVmx24i from '@/assets/machines/hurco-vmx24i.jpg';
import hurcoVm10i from '@/assets/machines/hurco-vm10i.jpg';
import metallkraft from '@/assets/machines/metallkraft-bandsaege.jpg';
import optimum from '@/assets/machines/optimum-bandsaege.jpg';
import onecnc from '@/assets/machines/onecnc-cadcam.jpg';
import tesaHite from '@/assets/machines/tesa-hite.jpg';
import praegearbeit from '@/assets/machines/praegearbeit.jpg';
import detail5achs01 from '@/assets/machines/5achs-detail-01.jpg';
import detail5achs02 from '@/assets/machines/5achs-detail-02.jpg';

interface Machine {
  id: number;
  name: string;
  description: string;
  image: string;
  specs?: string[];
}

const hurcoMachines: Machine[] = [
  {
    id: 2,
    name: 'Hurco VMX42Ui',
    description: 'Effiziente Komplettbearbeitung mit reduzierten Spannvorgängen',
    image: hurcoVmx42ui,
    specs: ['5 Achsen', '40 Werkzeuge', '12.000 U/min', 'IKZ', '1067x610x520mm'],
  },
  {
    id: 3,
    name: 'Hurco VMX24i',
    description: 'Wirtschaftliche Bearbeitung von Einzelteilen und Kleinserien',
    image: hurcoVmx24i,
    specs: ['3 Achsen', '30 Werkzeuge', '12.000 U/min', '610x510x610mm'],
  },
  {
    id: 4,
    name: 'Hurco VM10i Plus',
    description: 'Wirtschaftliche Bearbeitung von Einzelteilen und Kleinserien',
    image: hurcoVm10i,
    specs: ['3 Achsen', '20 Werkzeuge', '10.000 U/min', '660x406x508mm'],
  },
];

const otherMachines: Machine[] = [
  {
    id: 1,
    name: 'Fertigung',
    description: 'Moderne Fertigungshalle',
    image: werkstatt,
    specs: [],
  },
  {
    id: 7,
    name: 'OneCNC',
    description: 'CAD/CAM Software',
    image: onecnc,
    specs: ['CAD/CAM', '5-Achs Programmierung'],
  },
  {
    id: 9,
    name: 'Prägestation Fa. Lang',
    description: 'Spannsystem mit Prägetechnik',
    image: praegearbeit,
    specs: ['Wenig Spanndruck', 'Höchste Haltekraft', 'Für Rohteile'],
  },
  {
    id: 8,
    name: 'TESA-Hite',
    description: 'Höhenmessgerät',
    image: tesaHite,
    specs: ['Digitale Messung', 'Hochpräzise'],
  },
  {
    id: 5,
    name: 'Metallkraft',
    description: 'Automatische Bandsäge',
    image: metallkraft,
    specs: ['Automatisch', 'Bis 200mm'],
  },
  {
    id: 6,
    name: 'Optimum',
    description: 'Manuelle Bandsäge',
    image: optimum,
    specs: ['Manuell', 'Flexibel'],
  },
];

const MachineCarousel = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailIndex, setDetailIndex] = useState(0);

  const detailImages = [
    { img: detail5achs01, label: 'Bauteil bei 5-Achs Bearbeitung' },
    { img: detail5achs02, label: 'Komplexbauteil bei Bearbeitung' },
  ];
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true,
      align: 'start',
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="mb-16"
    >
      {/* Hurco Machines - Static Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {/* 5-Axis Card with Detail Collage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          whileHover={{ y: -6, scale: 1.02 }}
          className="bg-gradient-to-br from-[hsl(198,50%,36%)] to-[hsl(198,55%,28%)] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 relative group"
        >
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-xl group-hover:bg-white/15 transition-colors duration-500" />
          <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-white/5 blur-lg" />
          <div className="aspect-[4/3] overflow-hidden relative">
            <img loading="lazy" decoding="async" src={hurcoMachines[0].image} alt={`${hurcoMachines[0].name} CNC-Bearbeitungszentrum im Maschinenpark von SR-Zerspanung`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            {/* Detail Collage */}
            <div className="absolute bottom-2 right-2 flex gap-2">
              {detailImages.map((detail, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setDetailIndex(i); setDetailOpen(true); }}
                  className="w-24 h-24 md:w-14 md:h-14 lg:w-24 lg:h-24 rounded-lg overflow-hidden shadow-lg border-2 border-white/40 hover:border-white/80 transition-all hover:scale-105 cursor-pointer relative"
                >
                  <img loading="lazy" decoding="async" src={detail.img} alt={`${detail.label} – Detailaufnahme der CNC-Bearbeitung bei SR-Zerspanung`} className="w-full h-full object-cover brightness-110" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 px-1.5 py-1">
                    <p className="text-white text-[10px] leading-tight truncate">{detail.label}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="p-5 relative z-10">
            <h3 className="font-heading font-bold text-lg text-white">{hurcoMachines[0].name}</h3>
            <p className="text-white/70 text-sm mb-3">{hurcoMachines[0].description}</p>
            {hurcoMachines[0].specs && (
              <div className="flex flex-wrap gap-1.5">
                {hurcoMachines[0].specs.map((spec, index) => (
                  <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm border border-white/20">
                    {spec}
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* 3-Axis Machines */}
        {hurcoMachines.slice(1).map((machine, i) => (
          <motion.div
            key={machine.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: (i + 1) * 0.1 }}
            whileHover={{ y: -6, scale: 1.02 }}
            className="bg-gradient-to-br from-[hsl(198,50%,36%)] to-[hsl(198,55%,28%)] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 relative group"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-xl group-hover:bg-white/15 transition-colors duration-500" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-white/5 blur-lg" />
            <div className="aspect-[4/3] overflow-hidden">
              <img loading="lazy" decoding="async" src={machine.image} alt={`${machine.name} CNC-Bearbeitungszentrum im Maschinenpark von SR-Zerspanung`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
            </div>
            <div className="p-5 relative z-10">
              <h3 className="font-heading font-bold text-lg text-white">{machine.name}</h3>
              <p className="text-white/70 text-sm mb-3">{machine.description}</p>
              {machine.specs && (
                <div className="flex flex-wrap gap-1.5">
                  {machine.specs.map((spec, index) => (
                    <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm border border-white/20">
                      {spec}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Other Machines - Carousel */}
      <div className="relative">
        <button
          onClick={scrollPrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-card/90 backdrop-blur-sm border border-border rounded-full flex items-center justify-center shadow-lg hover:bg-card transition-colors -translate-x-1/2 md:-translate-x-4"
          aria-label="Vorherige Maschine"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-foreground" />
        </button>
        
        <button
          onClick={scrollNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-card/90 backdrop-blur-sm border border-border rounded-full flex items-center justify-center shadow-lg hover:bg-card transition-colors translate-x-1/2 md:translate-x-4"
          aria-label="Nächste Maschine"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-foreground" />
        </button>

        <div className="overflow-hidden rounded-2xl" ref={emblaRef}>
          <div className="flex">
            {otherMachines.map((machine) => (
              <div 
                key={machine.id} 
                className="flex-[0_0_100%] min-w-0 md:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-2 flex"
              >
                <div className="overflow-hidden rounded-xl bg-gradient-to-br from-[hsl(198,50%,36%)] to-[hsl(198,55%,28%)] hover:shadow-xl transition-shadow duration-300 group flex flex-col w-full">
                  <div className="aspect-[4/3] overflow-hidden flex-shrink-0">
                    <img
                      src={machine.image}
                      alt={`${machine.name} – Maschine im Maschinenpark von SR-Zerspanung`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-heading font-bold text-lg text-white">
                      {machine.name}
                    </h3>
                    <p className="text-white/70 text-sm mb-2">
                      {machine.description}
                    </p>
                    {machine.specs && machine.specs.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-auto">
                        {machine.specs.map((spec, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white backdrop-blur-sm border border-white/20"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-6">
          {otherMachines.map((_, index) => (
            <button
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                selectedIndex === index 
                  ? "bg-primary w-6" 
                  : "bg-border hover:bg-muted-foreground"
              )}
              aria-label={`Zu Maschine ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Detail Image Lightbox with swipe */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl p-4 bg-background overflow-y-auto max-h-[90vh]">
          <div className="relative select-none">
            <motion.img
              key={detailIndex}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              src={detailImages[detailIndex].img}
              alt={`${detailImages[detailIndex].label} – Detailaufnahme der CNC-Bearbeitung bei SR-Zerspanung`}
              className="w-full max-h-[70vh] object-contain rounded-lg brightness-110"
              draggable={false}
            />
            {/* Navigation arrows */}
            <button
              onClick={() => setDetailIndex((prev) => (prev === 0 ? detailImages.length - 1 : prev - 1))}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <button
              onClick={() => setDetailIndex((prev) => (prev === detailImages.length - 1 ? 0 : prev + 1))}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>
          <p className="text-center text-sm text-muted-foreground mt-2">{detailImages[detailIndex].label}</p>
          {/* Dots */}
          <div className="flex justify-center gap-2 mt-1">
            {detailImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setDetailIndex(i)}
                className={cn(
                  "w-2 h-2 rounded-full transition-all",
                  i === detailIndex ? "bg-primary w-5" : "bg-border"
                )}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default MachineCarousel;
